// Intent: [Conversion] [Journey] [SEO/GEO]
import React from "react";
import { CheckIcon, HammerIcon, HardHatIcon, RulerIcon } from "lucide-react";
import { PORTAL_REGISTER_URL } from "../config";
import { ButtonLink } from "../components/shared/ButtonLink";
import { PageHero } from "../components/shared/PageHero";
const paths = [{
  id: "trades",
  label: "Trades & cabinet installers",
  title: "Keep the job package clear from plan to submission.",
  copy: "For installers and trades who already understand cabinetry requirements and want a more direct ordering path.",
  start: "A room plan, cabinet list, measurements and specification details.",
  frustrations: ["Reformatting job information", "Waiting for manual quote exchanges", "Clarifications spread across email"],
  helps: ["Project-based job setup", "Visible configuration", "Saved job details", "A clear review point"],
  prepare: ["Final site dimensions", "Appliance data", "Materials and hardware requirements", "Site access information"],
  projects: "Kitchen, laundry, robe, vanity and multi-room cabinetry jobs.",
  cta: "Start Designing Online",
  analytics: "trade_account_clicked",
  icon: HammerIcon,
  style: "bg-pfpc-navy text-pfpc-offwhite"
}, {
  id: "builders",
  label: "Builders",
  title: "Create a repeatable cabinetry path for project work.",
  copy: "For builders coordinating selections, rooms and job information across new builds and renovations.",
  start: "A defined project, plans, room schedule and customer selections.",
  frustrations: ["Disconnected attachments", "Changing project details", "Unclear pricing checkpoints"],
  helps: ["Room-by-room structure", "Repeatable project information", "Selection visibility", "Human clarification when needed"],
  prepare: ["Current plans", "Selection schedule", "Appliance specifications", "Program and site details"],
  projects: "New builds, renovations and cabinetry across multiple rooms.",
  cta: "Start Designing Online",
  analytics: "builder_job_clicked",
  icon: HardHatIcon,
  style: "bg-pfpc-steel text-pfpc-offwhite"
}, {
  id: "diy",
  label: "DIY renovators",
  title: "Prepare carefully before you start configuring cabinets.",
  copy: "For capable renovators comfortable taking accurate measurements and making informed product decisions.",
  start: "A measured room, appliance details and a clear idea of the required storage.",
  frustrations: ["Not knowing what to measure", "Missing clearances", "Uncertainty before submission"],
  helps: ["Measurement-first guidance", "Plain-English preparation", "Visible review steps", "Austen as a support path"],
  prepare: ["Finished dimensions", "Services and openings", "Appliance model numbers", "Preferred finishes"],
  projects: "Prepared kitchen, laundry, wardrobe and vanity renovations.",
  cta: "Start Designing Online",
  analytics: "diy_project_clicked",
  icon: RulerIcon,
  style: "bg-pfpc-offwhite text-pfpc-navy"
}];
function WhoWeHelpPage() {
  return <main id="main-content" data-id="element-652" data-mp="4qrpoe6qido6"><PageHero eyebrow="Who we help" title="One platform. Three clearly defined ways to start." copy="Choose the guidance that matches your experience, project structure and responsibility for the final details." accent="orange" data-id="element-653" data-mp="4nvv8zu3i74h"><ButtonLink href="#trades" className="border-pfpc-navy bg-pfpc-navy text-pfpc-offwhite" data-id="element-654" data-mp="obrxunfjovb7">Find your pathway</ButtonLink></PageHero>
{paths.map(({
      id,
      label,
      title,
      copy,
      start,
      frustrations,
      helps,
      prepare,
      projects,
      cta,
      analytics,
      icon: Icon,
      style
    }, index) => <section id={id} key={id} className={`section-shell ${style}`} data-id="element-655" data-mp="f9r440jisrea" data-mpi={index}><div className="container-shell grid gap-10 lg:grid-cols-[.8fr_1.2fr] lg:gap-16" data-id="element-656" data-mp="mfu432zlfga"><div data-id="element-657" data-mp="kcs0kok7vv7"><div className={`grid h-14 w-14 place-items-center rounded-[10px] ${id === "diy" ? "bg-pfpc-orange/15 text-pfpc-orange" : "bg-pfpc-offwhite/10 text-pfpc-orange"}`} data-id="element-658" data-mp="kcwawvlei10"><Icon className="h-7 w-7" data-id="element-659" data-mp="3ns6p265g429c" /></div><p className={`section-label mt-6 ${id === "diy" ? "text-pfpc-orange" : "text-pfpc-offwhite/55"}`} data-id="element-660" data-mp="3np2fsud4igbt">{label}</p><h2 className="mt-3 font-heading text-[30px] font-extrabold leading-[1.15] md:text-[40px]" data-id="element-661" data-mp="3rq4ftdev6weu">{title}</h2><p className={`mt-5 text-[17px] leading-7 ${id === "diy" ? "text-pfpc-navy/68" : "text-pfpc-offwhite/68"}`} data-id="element-662" data-mp="3dv24ubv5ugv6">{copy}</p><ButtonLink href={PORTAL_REGISTER_URL} analytics={analytics} variant={id === "diy" ? "primary" : "secondaryDark"} className="mt-7" data-id="element-663" data-mp="ozt2d0vgrw8i">{cta}</ButtonLink><a href="/help/" className="ml-0 mt-3 inline-flex min-h-11 items-center px-3 text-sm font-bold underline underline-offset-4 sm:ml-2" data-id="element-664" data-mp="kzipd49985k5">Get ordering help</a></div>
<div className={`grid gap-px ${id === "diy" ? "bg-pfpc-navy/15" : "bg-pfpc-offwhite/15"} sm:grid-cols-2`} data-id="element-665" data-mp="b8o0ds4mirv0"><PathCell title="Typical starting point" text={start} light={id !== "diy"} data-id="element-666" data-mp="6933dn90ktkx" /><PathCell title="Suitable project types" text={projects} light={id !== "diy"} data-id="element-667" data-mp="678g42a9spdo" /><ListCell title="Main frustrations" items={frustrations} light={id !== "diy"} data-id="element-668" data-mp="18ots0rrlbks" /><ListCell title="What to prepare" items={prepare} light={id !== "diy"} data-id="element-669" data-mp="3tf2djov2ixsc" /><div className="sm:col-span-2" data-id="element-670" data-mp="3of7mnlbam87a"><ListCell title="How the platform helps" items={helps} light={id !== "diy"} horizontal data-id="element-671" data-mp="3ofzkl2sd4i0q" /></div></div></div></section>)}
</main>;
}
function PathCell({
  title,
  text,
  light
}: {
  title: string;
  text: string;
  light: boolean;
}) {
  return <div className={light ? "bg-pfpc-offwhite/[.06] p-6" : "bg-[#eeece6] p-6"} data-id="element-672" data-mp="1mow622fcqjaw"><h3 className={`section-label ${light ? "text-pfpc-orange" : "text-pfpc-steel"}`} data-id="element-673" data-mp="1u7lzgkhrq3ok">{title}</h3><p className={`mt-4 text-sm leading-6 ${light ? "text-pfpc-offwhite/72" : "text-pfpc-navy/70"}`} data-id="element-674" data-mp="1z5x9av82ce6s">{text}</p></div>;
}
function ListCell({
  title,
  items,
  light,
  horizontal = false
}: {
  title: string;
  items: string[];
  light: boolean;
  horizontal?: boolean;
}) {
  return <div className={light ? "bg-pfpc-offwhite/[.06] p-6" : "bg-[#eeece6] p-6"} data-id="element-675" data-mp="28uowyr63ccbw"><h3 className={`section-label ${light ? "text-pfpc-orange" : "text-pfpc-steel"}`} data-id="element-676" data-mp="1b8rhz3qx6p4p">{title}</h3><ul className={`mt-4 grid gap-2 ${horizontal ? "sm:grid-cols-2" : ""}`} data-id="element-677" data-mp="1g72rteh7szmx">{items.map(item => <li key={item} className={`flex gap-2 text-sm ${light ? "text-pfpc-offwhite/72" : "text-pfpc-navy/70"}`} data-id="element-678" data-mp="1l6fenz8366pi"><CheckIcon className="h-4 w-4 shrink-0 text-pfpc-orange" data-id="element-679" data-mp="1p80yy5my9n9q" />{item}</li>)}</ul></div>;
}
export { WhoWeHelpPage };